// İSG Portal — Genel Arama / typeahead (S34, tenant-scoped) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §34.3. Backend kanonu (`backend/src/Isgportal.Permits/Models/
// SearchDtos.cs` + `Isgportal.Api/Controllers/SearchController.cs`). **Yalnız tip + fonksiyon —
// bu dilim (S34-4) tüketmez**; ilk gerçek tüketici üst çubuk aramasıdır (S34-5).
//
// Üç gruplu (izin/taşeron/personel) arama; her grup KENDİ kapısına tabidir (§34 giriş tablosu)
// — yetkisiz grup `null`, eşleşmesi olmayan grup `{ items: [], hasMore: false }`. Yeni modül
// YOK, uç kapısı `permits/list`.
//
// **KVKK.** `personnel` grubu TC kimlik/e-posta/telefon üzerinde ARAMA YAPMAZ ve bu alanları
// DÖNMEZ (§34.3) — arama bir kimlik sorgulama aracına dönüşmez.

import { api } from './client';
import type { PermitStatus } from './permits';

/** Bir arama grubunun ortak zarfı — `hasMore` = `limit`'ten fazla eşleşme var mı (sayı verilmez). */
export interface SearchGroupDto<T> {
  items: T[];
  hasMore: boolean;
}

/** İzin eşleşmesi — sıra `validityStart` azalan (§14.1 liste sırası). */
export interface SearchPermitHitDto {
  uuid: string;
  title: string;
  /** TÜRETİLMİŞ durum (§14). */
  status: PermitStatus;
  contractorName: string;
}

/** Taşeron eşleşmesi — sıra ad artan (§13.1 liste sırası). */
export interface SearchContractorHitDto {
  uuid: string;
  name: string;
  taxNumber: string | null;
  status: 'Active' | 'Suspended' | 'Inactive';
}

/**
 * Personel eşleşmesi — `supplierUserUuid` = `SupplierUser.Uuid` (bağ/atama satırının kimliği
 * DEĞİL, §34.2 uyarısıyla AYNI tuzak). TC/e-posta/telefon DÖNMEZ (KVKK).
 */
export interface SearchPersonnelHitDto {
  supplierUserUuid: string;
  firstName: string;
  lastName: string;
  contractorUuid: string;
  contractorName: string;
}

/**
 * `GET /search` yanıtı — api-contract §34.3 (`SearchResultDto`). Her grup kendi kapısına tabidir;
 * yetkisiz grup `null` (alan yerindedir, değeri `null`).
 */
export interface SearchResultDto {
  /** Kapı: `permits/list` (+ §30.2 görünürlüğü). */
  permits: SearchGroupDto<SearchPermitHitDto> | null;
  /** Kapı: `contractors/list`. */
  contractors: SearchGroupDto<SearchContractorHitDto> | null;
  /** Kapı: `contractors/list` VE `contractors/get`. */
  personnel: SearchGroupDto<SearchPersonnelHitDto> | null;
}

/**
 * `GET /search?q=&limit=` parametreleri — api-contract §34.3. `q` zorunlu, trim sonrası 2–100
 * karakter (istemci 2 karakterden ÖNCE istek atmaz — debounce önerisi 250 ms). `limit` grup
 * başına azami kalem, varsayılan 5, `1..10`'a clamp (`400` değil).
 */
export interface SearchParams {
  q: string;
  limit?: number;
}

/**
 * `GET /search` (`permits/list` uç kapısı) — üç gruplu typeahead (§34.3). `q` trim sonrası
 * 2'den kısa/100'den uzun → `400 VALIDATION_FAILED` (istemci bu aralığın DIŞINDA istek atmamalı).
 *
 * `signal` (opsiyonel, additive; security review S34-5) — TanStack'in `queryFn({ signal })`ı
 * doğrudan geçilir, `client.ts`in dosya başlığı notundaki AYNI gerekçe: superseded bir
 * typeahead isteği tarayıcı seviyesinde İPTAL olur.
 */
export function search(params: SearchParams, options?: { signal?: AbortSignal }) {
  return api.get<SearchResultDto>('search', {
    searchParams: {
      q: params.q,
      limit: params.limit,
    },
    signal: options?.signal,
  });
}
